import React, { useEffect } from "react";

/**
 * Lightweight SEO helper — sets <title>, <meta description>, canonical,
 * Open Graph tags, and JSON-LD without depending on react-helmet.
 * Works for the CRA build because crawlers (Googlebot) execute JS and
 * read the updated DOM.
 */
const setMeta = (name, content, attr = "name") => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
  let el = document.head.querySelector(`script[data-seo="${id}"]`);
  if (data === null) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

export const SEO = ({
  title,
  description,
  canonical,
  image,
  type = "website",
  jsonLd,
  keywords,
}) => {
  useEffect(() => {
    if (title) document.title = title;
    setMeta("description", description);
    if (keywords && keywords.length) setMeta("keywords", keywords.join(", "));
    setLink("canonical", canonical);

    // Open Graph
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", type, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:image", image, "property");

    // Twitter
    setMeta("twitter:card", image ? "summary_large_image" : "summary");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image);

    // Structured data
    if (jsonLd) setJsonLd("page", jsonLd);
    return () => setJsonLd("page", null);
  }, [title, description, canonical, image, type, jsonLd, keywords]);
  return null;
};

export default SEO;
